/**
 * 評価データマッパー
 *
 * Firestoreのデータ構造がバージョンによって異なるため、
 * バージョンごとにマッパーを分離して管理する。
 */

import { DocumentData } from "firebase/firestore";
import {
  EvaluationListItem,
  EvaluationDetail,
  EvaluationDataFormat,
  EvaluationVersion,
  QuestionType,
  ClaimType,
} from "../../types";
import { mapV2_0_0ToListItem, mapV2_0_0ToDetail } from "./mapEvaluationV2_0_0";
import { mapV2_1_0ToListItem, mapV2_1_0ToDetail } from "./mapEvaluationV2_1_0";
import { mapV2_2_0ToListItem, mapV2_2_0ToDetail } from "./mapEvaluationV2_2_0";
import { mapV3_0_0ToListItem, mapV3_0_0ToDetail } from "./mapEvaluationV3_0_0";
import { getTimestampSeconds } from "./utils";

/**
 * 保存された evaluationVersion からデータ形式を決める対応表。
 *
 * v2.0.0 以降の評価APIは analyticsEvaluations に必ず evaluationVersion を書き込むため、
 * この表だけで判定できる（構造から推測してはいけない）。
 * 新しい評価バージョンを切ったら、マッパーと詳細ビューを追加したうえでここに1行足すこと。
 * 追加を忘れたレコードは "unsupported" になり、一覧では未対応バッジ、詳細ではエラーとして表に出る。
 * v1系（evaluationVersion 未保存）は表示対応を終了したため、この表には無く "unsupported" 扱いになる。
 */
const EVALUATION_VERSION_TO_DATA_FORMAT: Record<string, EvaluationVersion> = {
  "2.0.0": "v2.0.0",
  "2.1.0": "v2.1.0",
  "2.2.0": "v2.2.0",
  "3.0.0": "v3.0.0",
};

/**
 * レコードのデータ形式を判定する。
 *
 * 保存された evaluationVersion だけで決める。対応表に無い（未対応バージョン）、
 * もしくは evaluationVersion を持たない（v1系レガシー）レコードは "unsupported"。
 * 「形の近い既知バージョン」へ寄せることはしない。寄せると別バージョンが旧バージョンの
 * ビューで黙って描画され、欠けたフィールドに気づけないため。
 */
export function resolveDataFormat(data: DocumentData): EvaluationDataFormat {
  const storedEvaluationVersion = data.evaluationVersion;
  if (typeof storedEvaluationVersion === "string") {
    return EVALUATION_VERSION_TO_DATA_FORMAT[storedEvaluationVersion] ?? "unsupported";
  }
  return "unsupported";
}

/** 詳細ビューを持たないバージョンのレコードを詳細表示しようとしたときのエラー */
export class UnsupportedEvaluationVersionError extends Error {
  constructor(evaluationVersion: unknown) {
    const versionLabel =
      typeof evaluationVersion === "string"
        ? `評価バージョン "${evaluationVersion}"`
        : "旧形式のデータ";
    super(`${versionLabel} の詳細表示に未対応です。内容は下の生データ（JSON）で確認できます。`);
    this.name = "UnsupportedEvaluationVersionError";
  }
}

/**
 * バージョンに依存しない共通メタ情報。
 * 版数は分析用の記録であり評価結果の構造とは独立しているため、各バージョンのマッパーではなくここで付与する。
 */
function extractVersionMetadata(
  data: DocumentData
): Pick<EvaluationListItem, "evaluationVersion" | "promptVersion"> {
  return {
    evaluationVersion:
      typeof data.evaluationVersion === "string" ? data.evaluationVersion : undefined,
    promptVersion: typeof data.promptVersion === "number" ? data.promptVersion : undefined,
  };
}

/**
 * 未対応バージョンのレコードを一覧アイテムに変換する。
 *
 * 1件でも未対応があると一覧全体が見られなくなるのは困るため、
 * バージョンをまたいで名前が変わっていない共通フィールドだけを読んで行を組み立てる。
 * 構造からバージョンを推測することはしない。
 */
function mapUnsupportedVersionToListItem(docId: string, data: DocumentData): EvaluationListItem {
  const userOpinion = data.input?.userOpinion || "";
  const theme = data.topic?.theme || "";

  return {
    id: docId,
    version: "unsupported",
    timestamp: { _seconds: getTimestampSeconds(data.timestamp) },
    topic: {
      id: data.topic?.id || "",
      content: data.topicContent || theme,
      genre: data.topic?.genre || "",
      difficulty: data.topic?.difficulty || 0,
      theme,
      questionType: (data.questionType as QuestionType) || undefined,
      claimType: (data.claimType as ClaimType) || undefined,
      background: data.topic?.background || undefined,
    },
    userOpinionPreview:
      userOpinion.length > 100 ? userOpinion.substring(0, 100) + "..." : userOpinion,
    totalScore: data.result?.totalScore || 0,
    appVersion: data.app_version || "",
  };
}

/**
 * Firestoreデータを一覧アイテムに変換
 */
export function mapToListItem(docId: string, data: DocumentData): EvaluationListItem {
  const dataFormat = resolveDataFormat(data);
  const versionMetadata = extractVersionMetadata(data);

  switch (dataFormat) {
    case "v3.0.0":
      return { ...mapV3_0_0ToListItem(docId, data), ...versionMetadata };
    case "v2.2.0":
      return { ...mapV2_2_0ToListItem(docId, data), ...versionMetadata };
    case "v2.1.0":
      return { ...mapV2_1_0ToListItem(docId, data), ...versionMetadata };
    case "v2.0.0":
      return { ...mapV2_0_0ToListItem(docId, data), ...versionMetadata };
    case "unsupported":
      return { ...mapUnsupportedVersionToListItem(docId, data), ...versionMetadata };
  }
}

/**
 * Firestoreデータを詳細に変換
 */
export function mapToDetail(docId: string, data: DocumentData): EvaluationDetail {
  const dataFormat = resolveDataFormat(data);

  switch (dataFormat) {
    case "v3.0.0":
      return mapV3_0_0ToDetail(docId, data);
    case "v2.2.0":
      return mapV2_2_0ToDetail(docId, data);
    case "v2.1.0":
      return mapV2_1_0ToDetail(docId, data);
    case "v2.0.0":
      return mapV2_0_0ToDetail(docId, data);
    case "unsupported":
      throw new UnsupportedEvaluationVersionError(data.evaluationVersion);
  }
}
